import { AutosaveStatus } from '@components/AutosaveStatus';
import { practiceSettingsSubtitleSx } from '@features/organizations/components/PracticeSettings/practiceSettingsTypography';
import { Alert, Box, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material';

import type { SubjectLanguage } from '../types';

export type CopyChannel = 'sms' | 'email';

export interface RequestCopyDraft {
  smsBody: string;
  emailSubject: string;
  emailBody: string;
}

export interface SmsSegmentHint {
  characters: number;
  segments: number;
}

export interface RequestCopyEditorProps {
  draft: RequestCopyDraft;
  /**
   * True while the practice has never saved copy and this is the seed, so
   * there is nothing to reset.
   *
   * ONE flag for both channels, because that is what the endpoint reports
   * (GDN-3450). The write accepts a per-channel reset — omitting a channel
   * restores its seed — but the read cannot say WHICH channel was customized,
   * so the editor cannot honestly offer per-channel reset. Raised on the
   * issue; document-level reset is what the data supports today.
   */
  smsIsDefault: boolean;
  emailIsDefault: boolean;
  /**
   * The token the server refuses a save for. Used to warn BEFORE the round
   * trip: the write rejects copy containing the link, and finding that out
   * from a failed save is a worse way to learn it than being told while typing.
   *
   * Read from the response rather than hardcoded — a literal that works today
   * and stops working silently when the token changes is exactly the kind of
   * guess this surface has been avoiding.
   */
  linkPlaceholder?: string;
  /** The SMS as the patient will receive it, link included. */
  smsPreview?: string;
  /** True when the preview is the SAVED message rather than the draft. */
  previewIsStale?: boolean;
  /**
   * Stated, never enforced (GDN-3450). Undefined when the server did not send
   * it, and then no hint is shown rather than a computed one: segmentation
   * depends on the encoding the carrier picks, which the browser cannot know.
   */
  smsSegmentHint?: SmsSegmentHint;
  /**
   * Practice admin. A provider who cannot edit still reads the copy, because
   * the launch preview is not admin-only — they get the text without the
   * inputs rather than inputs that refuse them.
   */
  canEdit: boolean;
  status?: 'idle' | 'saving' | 'saved' | 'error';
  /** Unsaved edits are pending. Drives the shared autosave indicator. */
  isDirty?: boolean;
  /** What the server said when it refused the save. Shown verbatim in context. */
  errorMessage?: string;
  subject: SubjectLanguage;
  onChange?: (next: RequestCopyDraft) => void;
  onSave?: () => void;
  /** Restores the seeded copy for both channels. */
  onResetChannel?: (channel: CopyChannel) => void;
}

/**
 * The practice's default request copy (AC-11), edited in Settings and used by
 * every request — single sends from the chart and bulk launches alike.
 *
 * The secure link is not in here, and that is the criterion holding by
 * construction rather than by trusting the editor: the saved text is rendered
 * into a template that appends the link, so no edit can remove it (PM ruling on
 * GDN-3450). The editor says so where an admin will read it, and the preview
 * shows the link as its own element rather than pasting it into the body —
 * the exact composed wording belongs to the template, and a preview that
 * guessed at it would be lying with more confidence than one that does not.
 */
export const RequestCopyEditor = ({
  draft,
  smsIsDefault,
  emailIsDefault,
  linkPlaceholder,
  smsPreview,
  previewIsStale = false,
  smsSegmentHint,
  canEdit,
  status = 'idle',
  isDirty = false,
  errorMessage,
  subject,
  onChange,
  onSave,
  onResetChannel,
}: RequestCopyEditorProps) => {
  const update = (patch: Partial<RequestCopyDraft>) => onChange?.({ ...draft, ...patch });

  /**
   * The save is refused when the copy contains the secure link, so say so
   * while it is being typed rather than after the round trip.
   *
   * Only the placeholder token is detected, and only when the server told us
   * what it is. A general URL sweep would over-warn — a practice may
   * legitimately link its own site — and hardcoding the token would work today
   * and fail silently the day it changes.
   */
  const pastedLink = Boolean(
    linkPlaceholder &&
    [draft.smsBody, draft.emailSubject, draft.emailBody].some(text =>
      text.includes(linkPlaceholder),
    ),
  );

  return (
    <Box
      component="section"
      aria-labelledby="requirement-default-messages"
      sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}
    >
      <Typography
        id="requirement-default-messages"
        component="h2"
        variant="h6"
        sx={{ fontWeight: 650 }}
      >
        Default messages
      </Typography>
      <Typography variant="body2" sx={{ ...practiceSettingsSubtitleSx, mb: 3 }}>
        Customize the text and email used when you request items from {subject.plural}. Each message
        includes a secure link.
      </Typography>

      {pastedLink && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Remove {linkPlaceholder} from your text — the secure link is added to every message
          automatically, and a message containing it cannot be saved.
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {errorMessage}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
          gap: { xs: 3, lg: 4 },
          alignItems: 'start',
        }}
      >
        <ChannelBlock
          title="Text message"
          isDefault={smsIsDefault}
          canEdit={canEdit}
          onReset={() => onResetChannel?.('sms')}
        >
          {canEdit ? (
            <TextField
              label="Text message"
              value={draft.smsBody}
              onChange={event => update({ smsBody: event.target.value })}
              onBlur={onSave}
              multiline
              minRows={3}
              fullWidth
              size="small"
            />
          ) : (
            <ReadOnlyText text={draft.smsBody} />
          )}

          {smsSegmentHint && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {smsSegmentHint.characters} characters ·{' '}
              {smsSegmentHint.segments === 1 ? '1 text' : `${smsSegmentHint.segments} texts`}
            </Typography>
          )}

          {smsPreview && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                {previewIsStale
                  ? `What ${subject.plural} receive — refreshes after saving`
                  : `What ${subject.plural} receive`}
              </Typography>
              <Typography variant="body2">{smsPreview}</Typography>
            </Paper>
          )}

          <LinkNotice />
        </ChannelBlock>

        <ChannelBlock
          title="Email"
          isDefault={emailIsDefault}
          canEdit={canEdit}
          onReset={() => onResetChannel?.('email')}
        >
          {canEdit ? (
            <>
              <TextField
                label="Subject"
                value={draft.emailSubject}
                onChange={event => update({ emailSubject: event.target.value })}
                onBlur={onSave}
                fullWidth
                size="small"
              />
              <TextField
                label="Body"
                value={draft.emailBody}
                onChange={event => update({ emailBody: event.target.value })}
                onBlur={onSave}
                multiline
                minRows={3}
                fullWidth
                size="small"
              />
            </>
          ) : (
            <>
              <ReadOnlyText text={draft.emailSubject} label="Subject" />
              <ReadOnlyText text={draft.emailBody} />
            </>
          )}

          <LinkNotice />
        </ChannelBlock>
      </Box>

      {/* The shared indicator, not a private one. The sibling catalog row
          (RequirementDefinitionRow) already uses it, and two Settings surfaces
          in the same feature showing save state two different ways is exactly
          the drift the convention exists to stop. */}
      <Box sx={{ mt: 1 }}>
        <AutosaveStatus status={status} isDirty={isDirty} verbose />
      </Box>
    </Box>
  );
};

/**
 * Where the link goes, said once per channel.
 *
 * Not pasted into the body text: the template owns the exact wording and
 * position, and composing a plausible version here would show an admin a
 * message that is not the one their patients receive.
 */
const LinkNotice = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.25,
      py: 0.75,
      borderRadius: 1,
      bgcolor: 'action.hover',
    }}
  >
    <Chip size="small" label="Secure link" />
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      Added to the end of every message automatically. It cannot be removed, and you do not need to
      paste it in.
    </Typography>
  </Box>
);

const ReadOnlyText = ({ text, label }: { text: string; label?: string }) => (
  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
    {label && (
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        {label}
      </Typography>
    )}
    <Typography variant="body2">{text}</Typography>
  </Paper>
);

const ChannelBlock = ({
  title,
  isDefault,
  canEdit,
  onReset,
  children,
}: {
  title: string;
  isDefault: boolean;
  canEdit: boolean;
  onReset: () => void;
  children: React.ReactNode;
}) => (
  <Box>
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5, minHeight: 30 }}
    >
      <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {/* Per channel, because the two are independently owned: resetting the
          text message must not offer to discard an email the practice wrote.
          Offered only when there is something to undo — a Reset on untouched
          seeded copy does nothing, and a control that does nothing is a
          question the admin has to answer for themselves. */}
      {canEdit && !isDefault && (
        <Button size="small" variant="text" onClick={onReset} sx={{ textTransform: 'none' }}>
          Reset to default
        </Button>
      )}
    </Stack>
    <Stack spacing={1}>{children}</Stack>
  </Box>
);
